"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import { Note } from "@/lib/music/note";

const PREVIEW_HOLD_MS = 900;
const PREVIEW_VELOCITY = 0.5;

/**
 * Joue un voicing MIDI sur le piano du studio pendant ~900 ms, puis
 * relâche les notes. Pendant de `useChordPreview` (qui prend des pcs et
 * les place en C4), ici on accepte des numéros MIDI bruts pour pouvoir
 * écouter les voicings déjà choisis (basse main gauche + voix main
 * droite) de la vue progression.
 *
 * Renvoie aussi `isPreviewing` : pendant la prévisualisation, le piano
 * du studio émet de vrais events `press` (ce qui est nécessaire pour
 * faire jouer le synthé partagé), donc l'auto-advance harmonique
 * verrait les notes comme « jouées par l'élève ». Le caller doit
 * neutraliser sa détection de complétion tant que `isPreviewing` est
 * vrai pour ne pas avancer toute la progression.
 */
export function useVoicingPreview(): {
  play: (midis: ReadonlyArray<number>) => void;
  isPreviewing: boolean;
} {
  const { studio } = useStudio();
  const heldRef = useRef<Note[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      const piano = studio?.getPiano();

      if (piano) {
        for (const n of heldRef.current) {
          piano.release(n, "app");
        }
      }

      heldRef.current = [];
    };
  }, [studio]);

  const play = useCallback(
    (midis: ReadonlyArray<number>) => {
      if (!studio || midis.length === 0) {
        return;
      }

      const piano = studio.getPiano();

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      for (const n of heldRef.current) {
        piano.release(n, "app");
      }

      const notes: Note[] = midis.map((m) => Note.fromMidi(m));
      heldRef.current = notes;

      for (const n of notes) {
        piano.press(n, PREVIEW_VELOCITY, "app");
      }

      setIsPreviewing(true);

      timerRef.current = setTimeout(() => {
        for (const n of notes) {
          piano.release(n, "app");
        }

        heldRef.current = [];
        timerRef.current = null;
        setIsPreviewing(false);
      }, PREVIEW_HOLD_MS);
    },
    [studio],
  );

  return { play, isPreviewing };
}
